import { useErrorReporter } from '@mdlinx/frontend-shared';
import {
  SocialLoginResult,
  useLoginWithFacebookAccount,
  useLoginWithGoogleAccount,
  useLoginWithLinkedInAccount,
} from 'lib/effect-hooks';
import { getAdditionalUserInfo } from 'firebase/auth';
import { messages } from 'lib/messages';
import * as React from 'react';
import { SocialLoginButtons, ErrorNotification } from '@mdlinx/component-library';
import { Box } from 'rebass';
import { trackEvent } from '../../lib/analytics';
import { RegistrationTrackingData } from 'lib/tracking';

type Props = {
  onSocialSignUp: (providerUserProfile?: ProviderUserProfile) => void;
  onSocialLogin: () => void;
  isSignUp?: boolean;
  setLoading?: (isLoading: boolean) => void;
  registrationTrackingData: RegistrationTrackingData;
};

type GoogleProfile = {
  given_name: string;
  family_name: string;
  email: string;
};

type FacebookProfile = {
  first_name: string;
  last_name: string;
  email: string;
};

export interface ProviderUserProfile {
  email: string;
  firstName: string;
  lastName: string;
}

export const SocialAccountForm: React.FC<Props> = ({
  onSocialSignUp,
  onSocialLogin,
  setLoading = () => {},
  isSignUp,
  registrationTrackingData,
}) => {
  const errorReporter = useErrorReporter();
  const loginWithFacebookAccount = useLoginWithFacebookAccount({ registrationTrackingData });
  const loginWithGoogleAccount = useLoginWithGoogleAccount({ registrationTrackingData });
  const loginWithLinkedInAccount = useLoginWithLinkedInAccount({ registrationTrackingData });
  const [error, setError] = React.useState<string | undefined>();

  function handleSocialAccountLogin(login: () => Promise<SocialLoginResult>) {
    setError(undefined);
    setLoading(true);

    login().then(
      result => {
        if (result.success) {
          // This should always be true if result.success
          const { credential } = result.data;
          const additionalUserInfo = credential ? getAdditionalUserInfo(credential) : null;
          if (credential && additionalUserInfo) {
            const { profile, isNewUser, providerId } = additionalUserInfo;

            // Track registration/login event
            trackEvent(isNewUser ? 'auth_social_sign_up_succeeded' : 'auth_social_login_succeeded', {
              providerId: providerId ?? '',
            });

            if (isNewUser) {
              if (providerId === 'google.com' && profile) {
                const { given_name, family_name, email } = profile as GoogleProfile;
                return onSocialSignUp({
                  firstName: given_name || '',
                  lastName: family_name || '',
                  email: email || '',
                });
              }

              if (providerId === 'facebook.com' && profile) {
                const { first_name, last_name, email } = profile as FacebookProfile;
                return onSocialSignUp({
                  firstName: first_name || '',
                  lastName: last_name || '',
                  email: email || '',
                });
              }

              return onSocialSignUp({
                firstName: '',
                lastName: '',
                email: '',
              });
            }
          }
          onSocialLogin();
        } else {
          setLoading(false);
          trackEvent(isSignUp ? 'auth_social_sign_up_failed' : 'auth_social_login_failed');
          switch (result.error) {
            case 'CANCELED':
              return;
            case 'POPUP_CLOSED':
              return;
            case 'STATE_UNMATCHED':
              return setError(messages.error.login.stateUnmatched);
            case 'POPUP_BLOCKED':
              return setError(messages.error.login.popupBlocked);
            case 'ANOTHER_ACCOUNT_EXISTS':
              return setError(messages.error.login.accountConflicts);
            case 'UNKNOWN':
              return setError(messages.error.login.unknown);
          }
        }
      },
      e => {
        errorReporter.report(e);
        setError(messages.error.login.unknown);
      },
    );
  }

  return (
    <>
      {error && (
        <Box mb="20px">
          <ErrorNotification errorMessage={error} />
        </Box>
      )}
      <SocialLoginButtons
        onClickLinkedIn={() => {
          trackEvent('auth_click', { action: `social_${isSignUp ? 'sign_up' : 'login'}_linkedIn` });
          handleSocialAccountLogin(loginWithLinkedInAccount);
        }}
        onClickGoogle={() => {
          trackEvent('auth_click', { action: `social_${isSignUp ? 'sign_up' : 'login'}_google` });
          handleSocialAccountLogin(loginWithGoogleAccount);
        }}
        onClickFacebook={() => {
          trackEvent('auth_click', { action: `social_${isSignUp ? 'sign_up' : 'login'}_facebook` });
          handleSocialAccountLogin(loginWithFacebookAccount);
        }}
        isSignUp={isSignUp}
      />
    </>
  );
};

export const SocialPopupOverlay = ({ isOverlayVisible }: { isOverlayVisible: boolean }) => (
  <Box
    bg="white"
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: isOverlayVisible ? 'block' : 'none',
      opacity: 0.8,
    }}
  />
);
